import { Intent } from "@blueprintjs/core";
import {
    Advanced_Study_Constraint_Stacks,
    GetAdvancedStudiesQuery,
    GetAdvancedStudyResultsQuery,
    Maybe
} from "generated/graphql";
import { assertMaybeToValue } from "graphql/helpers/queryHelpers";
import { NiraBusId } from "./busType";
import { MonitoredFacilityId } from "./monitoredFacilityType";

const STUDY_LOAD = "Study Load" as const;
const BENCH_LOAD = "Bench Load" as const;
const FACILITY_TYPES = [STUDY_LOAD, BENCH_LOAD] as const;
export type FacilityType = (typeof FACILITY_TYPES)[number];

export const DEFAULT_MW_SIZE = 100;

const isKnownFacilityType = (
    facilityType: string
): facilityType is FacilityType => {
    return (FACILITY_TYPES as readonly string[]).includes(facilityType);
};

const convertToFacilityType = (facilityType: string): FacilityType => {
    if (isKnownFacilityType(facilityType)) {
        return facilityType;
    }

    throw new Error(`Unexpected facility type: ${facilityType}`);
};

export type AdvancedStudyId = string;

export enum AdvancedStudyStatus {
    RUN_REQUESTED = "RUN_REQUESTED",
    RUN_SUBMITTED = "RUN_SUBMITTED",
    RUNNING = "RUNNING",
    COMPLETED = "COMPLETED",
    FAILED = "FAILED"
}

type HasuraAdvancedStudy = {
    readonly advanced_study_id: string;
    readonly title: string;
    readonly status: string;
    readonly advanced_study_loads: HasuraAdvancedStudyLoad[];
};

type HasuraAdvancedStudyLoad = {
    readonly nira_bus_id: string;
    readonly mw_size: number;
    readonly bus?: Maybe<HasuraAdvancedStudyBus>;
    readonly facility_type: string;
};

type HasuraAdvancedStudyBus = {
    readonly bus_display_name?: Maybe<string>;
};

type HasuraAdvancedStudyConstraintStack = Readonly<
    Pick<
        Advanced_Study_Constraint_Stacks,
        | "monitored_facility_id"
        | "monitored_facility_name"
        | "rating"
        | "bench_percent_loading"
        | "study_percent_loading"
        | "cost"
        | "nira_bus_id"
        | "dfax"
        | "contingency"
    >
>;

export type AdvancedStudy = {
    readonly advancedStudyId: AdvancedStudyId;
    readonly title: string;
    readonly status: AdvancedStudyStatus;
    readonly loads: AdvancedStudyLoad[];
};

export type AdvancedStudyLoad = {
    readonly niraBusId: NiraBusId;
    readonly busDisplayName: string;
    readonly studyMwSize?: number;
    readonly benchMwSize?: number;
};

type AdvancedStudyDetails = {
    readonly status: AdvancedStudyStatus;
    readonly constraintStacks: ReadonlyArray<AdvancedStudyConstraintStack>;
};

export type AdvancedStudyConstraintStack = {
    readonly monitoredFacilityId: MonitoredFacilityId;
    readonly monitoredFacilityName: string;
    readonly rating: number;
    readonly benchPercentLoading: number;
    readonly studyPercentLoading: number;
    readonly cost: number;
    readonly niraBusId: NiraBusId;
    readonly dfax: number;
    readonly contingency: string;
};

const isKnownAdvancedStudyStatus = (
    status: string
): status is AdvancedStudyStatus => {
    return Object.values(AdvancedStudyStatus).includes(
        status as AdvancedStudyStatus
    );
};

const convertToAdvancedStudyStatus = (status: string): AdvancedStudyStatus => {
    if (isKnownAdvancedStudyStatus(status)) {
        return status;
    }

    throw new Error("Unexpected advanced study status");
};

export const convertAdvancedStudyLoadsToHasuraLoads = (
    loads: AdvancedStudyLoad[]
): Pick<
    HasuraAdvancedStudyLoad,
    "nira_bus_id" | "mw_size" | "facility_type"
>[] => {
    const entries: Pick<
        HasuraAdvancedStudyLoad,
        "nira_bus_id" | "mw_size" | "facility_type"
    >[] = [];

    for (const load of loads) {
        if (load.studyMwSize !== undefined) {
            entries.push({
                nira_bus_id: load.niraBusId,
                mw_size: load.studyMwSize,
                facility_type: STUDY_LOAD
            });
        }
        if (load.benchMwSize !== undefined) {
            entries.push({
                nira_bus_id: load.niraBusId,
                mw_size: load.benchMwSize,
                facility_type: BENCH_LOAD
            });
        }
    }

    return entries;
};

const groupHasuraLoads = (
    hasuraLoads: HasuraAdvancedStudyLoad[]
): AdvancedStudyLoad[] => {
    const busMap = new Map<string, AdvancedStudyLoad>();

    for (const load of hasuraLoads) {
        const existing = busMap.get(load.nira_bus_id);
        const facilityType = convertToFacilityType(load.facility_type);
        const mwField =
            facilityType === STUDY_LOAD ? "studyMwSize" : "benchMwSize";

        if (existing) {
            busMap.set(load.nira_bus_id, {
                ...existing,
                [mwField]: load.mw_size
            });
        } else {
            busMap.set(load.nira_bus_id, {
                niraBusId: load.nira_bus_id,
                busDisplayName: load.bus?.bus_display_name ?? "Unknown",
                [mwField]: load.mw_size
            });
        }
    }

    return Array.from(busMap.values());
};

const convertHasuraAdvancedStudyToNiraAdvancedStudy = (
    hasuraAdvancedStudy: HasuraAdvancedStudy
): AdvancedStudy => {
    return {
        advancedStudyId: hasuraAdvancedStudy.advanced_study_id,
        title: hasuraAdvancedStudy.title,
        status: convertToAdvancedStudyStatus(hasuraAdvancedStudy.status),
        loads: groupHasuraLoads(hasuraAdvancedStudy.advanced_study_loads)
    };
};

const convertHasuraAdvancedStudyConstraintStackToNiraAdvancedStudyConstraintStack =
    (
        hasuraConstraintStack: HasuraAdvancedStudyConstraintStack
    ): AdvancedStudyConstraintStack => {
        return {
            monitoredFacilityId: hasuraConstraintStack.monitored_facility_id,
            monitoredFacilityName:
                hasuraConstraintStack.monitored_facility_name,
            rating: hasuraConstraintStack.rating,
            benchPercentLoading: hasuraConstraintStack.bench_percent_loading,
            studyPercentLoading: hasuraConstraintStack.study_percent_loading,
            cost: hasuraConstraintStack.cost,
            niraBusId: hasuraConstraintStack.nira_bus_id,
            dfax: hasuraConstraintStack.dfax,
            contingency: hasuraConstraintStack.contingency
        };
    };

export const convertHasuraAdvancedStudiesToNiraAdvancedStudies = (
    hasuraData: GetAdvancedStudiesQuery
): ReadonlyArray<AdvancedStudy> => {
    return hasuraData.advanced_studies.map((hasuraStudy) =>
        convertHasuraAdvancedStudyToNiraAdvancedStudy(hasuraStudy)
    );
};

export const convertHasuraAdvancedStudyResultsToNiraAdvancedStudyDetails = (
    hasuraData: GetAdvancedStudyResultsQuery
): AdvancedStudyDetails => {
    const advancedStudy = assertMaybeToValue(hasuraData.advanced_studies_by_pk);
    return {
        status: convertToAdvancedStudyStatus(advancedStudy.status),
        constraintStacks: advancedStudy.advanced_study_constraint_stacks.map(
            convertHasuraAdvancedStudyConstraintStackToNiraAdvancedStudyConstraintStack
        )
    };
};

export const STATUS_TO_INTENT: Record<AdvancedStudyStatus, Intent> = {
    [AdvancedStudyStatus.RUN_REQUESTED]: Intent.NONE,
    [AdvancedStudyStatus.RUN_SUBMITTED]: Intent.NONE,
    [AdvancedStudyStatus.RUNNING]: Intent.PRIMARY,
    [AdvancedStudyStatus.COMPLETED]: Intent.SUCCESS,
    [AdvancedStudyStatus.FAILED]: Intent.DANGER
};

export const STATUS_TO_DISPLAY_STRING: Record<AdvancedStudyStatus, string> = {
    [AdvancedStudyStatus.RUN_REQUESTED]: "Run Requested",
    [AdvancedStudyStatus.RUN_SUBMITTED]: "Run Submitted",
    [AdvancedStudyStatus.RUNNING]: "Running",
    [AdvancedStudyStatus.COMPLETED]: "Completed",
    [AdvancedStudyStatus.FAILED]: "Failed"
};
